import { Show, createSignal, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Text } from '~ui/text';
import { Button } from '~ui/button';
import { Divider } from '~ui/divider';
import { Switch } from '~ui/switch';
import { NumberBox } from '~ui/field';
import { Table, type Column } from '~ui/table';
import { formatCurrency, formatDay, formatNumber, formatPercent } from '~/lib/format';
import { amount, asOf, backtests, records, reinvest, setAmount, setReinvest, unpriced, type Backtest, type FundRecord } from '~/stores/history-store';

type View = 'erosion' | 'backtest';

const signed = (n: number | null): 'green' | 'red' | undefined => (n == null ? undefined : n >= 0 ? 'green' : 'red');

const years = (n: number): string => `${formatNumber(n, { maximumFractionDigits: 1 })}y`;

const EROSION_COLUMNS: Column<FundRecord>[] = [
    { id: 'symbol', header: 'Symbol', size: 0.6, cell: (r) => <Text weight='medium'>{r().symbol}</Text> },
    { id: 'since', header: 'Since', size: 0.9, cell: (r) => <Text tone='muted'>{formatDay(r().from)}</Text> },
    { id: 'years', header: 'Held', align: 'right', size: 0.5, cell: (r) => <Text tone='muted'>{years(r().years)}</Text> },
    { id: 'first', header: 'First close', align: 'right', size: 0.8, cell: (r) => formatCurrency(r().firstClose) },
    { id: 'last', header: 'Last close', align: 'right', size: 0.8, cell: (r) => formatCurrency(r().lastClose) },
    // The headline pair. Capital is what the unit price did on its own; income
    // is what it paid out. A fund can be deep red on the left and still ahead
    // overall, and that is exactly the trade worth seeing whole.
    { id: 'capital', header: 'Capital', align: 'right', size: 0.8, cell: (r) => <Text tone={signed(r().capitalPct)}>{formatPercent(r().capitalPct, 1)}</Text> },
    { id: 'paid', header: 'Paid / unit', align: 'right', size: 0.8, cell: (r) => <Text tone='green'>{formatCurrency(r().distPerUnit)}</Text> },
    { id: 'income', header: 'Income', align: 'right', size: 0.7, cell: (r) => <Text tone='green'>{formatPercent(r().incomePct, 1)}</Text> },
    { id: 'total', header: 'Total', align: 'right', size: 0.8, cell: (r) => <Text tone={signed(r().totalPct)} weight='medium'>{formatPercent(r().totalPct, 1)}</Text> },
    {
        id: 'erosion',
        header: 'Was your own money',
        align: 'right',
        size: 1.2,
        // Red past a third: at that point a meaningful slice of the "income" was
        // the fund handing back capital it had lost.
        cell: (r) => <Text tone={r().erosionShare == null ? 'muted' : r().erosionShare! >= 33 ? 'red' : undefined}>{formatPercent(r().erosionShare, 0)}</Text>,
    },
];

const BACKTEST_COLUMNS: Column<Backtest>[] = [
    { id: 'symbol', header: 'Symbol', size: 0.6, cell: (b) => <Text weight='medium'>{b().symbol}</Text> },
    { id: 'since', header: 'Since', size: 0.9, cell: (b) => <Text tone='muted'>{formatDay(b().from)}</Text> },
    { id: 'years', header: 'Held', align: 'right', size: 0.5, cell: (b) => <Text tone='muted'>{years(b().years)}</Text> },
    { id: 'units', header: 'Units', align: 'right', size: 0.8, cell: (b) => formatNumber(b().startUnits, { maximumFractionDigits: 1 }) },
    { id: 'endunits', header: 'Units now', align: 'right', size: 0.8, cell: (b) => formatNumber(b().endUnits, { maximumFractionDigits: 1 }) },
    { id: 'received', header: 'Received', align: 'right', size: 0.9, cell: (b) => <Text tone='green'>{formatCurrency(b().received)}</Text> },
    { id: 'cash', header: 'Cash held', align: 'right', size: 0.9, cell: (b) => <Text tone={b().cash > 0 ? undefined : 'muted'}>{formatCurrency(b().cash)}</Text> },
    { id: 'value', header: 'Worth now', align: 'right', size: 1, cell: (b) => <Text weight='medium'>{formatCurrency(b().endValue)}</Text> },
    { id: 'profit', header: 'Profit', align: 'right', size: 0.9, cell: (b) => <Text tone={signed(b().profit)}>{formatCurrency(b().profit)}</Text> },
    {
        id: 'return',
        header: 'Return',
        align: 'right',
        size: 1,
        cell: (b) => (
            <Text tone={signed(b().returnPct)} weight='medium'>
                {formatPercent(b().returnPct, 1)} · {formatPercent(b().perYearPct, 1)}/yr
            </Text>
        ),
    },
];

const History: Component = () => {
    const [view, setView] = createSignal<View>('erosion');

    const Tab: Component<{ id: View; label: string }> = (props) => (
        // Trailing `!` — Button's own hover:bg-muted is emitted later in the
        // sheet and would otherwise win. Same note as dividends.tsx.
        <Button size='fill' label={props.label} class={view() === props.id ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''} onClick={() => setView(props.id)} />
    );

    return (
        <Panel>
            <Toolbar>
                {/* No pane title. The lit nav row already names the page,
                    and the tabs are the only thing here that says anything new. */}
                <ToolbarSection>
                    <Tab id='erosion' label='Erosion' />
                    <Tab id='backtest' label='Backtest' />

                    <Show when={view() === 'backtest'}>
                        <Divider />
                        <NumberBox value={amount()} onValue={setAmount} prefix='$' width='w-28' border='x' />
                        <div class='flex h-full items-center gap-2 px-2'>
                            <Text tone='muted'>Reinvest</Text>
                            <Switch checked={reinvest()} onChange={setReinvest} label='Reinvest distributions' />
                        </div>
                    </Show>
                </ToolbarSection>

                <ToolbarSection>
                    {/* Naming what is missing rather than letting ten funds read
                        as the whole pool. This empties itself as prices are
                        scraped for more of the universe. */}
                    <Show when={unpriced().length > 0}>
                        <Text tone='red' class='whitespace-nowrap pr-2'>
                            No price history: {unpriced().join(', ')}
                        </Text>
                    </Show>
                    <Text tone='muted' class='whitespace-nowrap pr-2'>
                        {view() === 'erosion' ? 'from each fund’s first bar on file · closes only' : 'bought at the first close, held to the last · closes only'}
                        {asOf() ? ` · to ${formatDay(asOf())}` : ''}
                    </Text>
                </ToolbarSection>
            </Toolbar>

            <Show when={view() === 'erosion'}>
                <Table
                    columns={EROSION_COLUMNS}
                    rows={records()}
                    empty='No fund has both a price history and a payout on file'
                    footer={
                        <Text tone='muted'>
                            Capital is the unit price alone; income is everything paid out over the same window. “Was your own money” is the share of the payout that only replaced the price it lost.
                        </Text>
                    }
                />
            </Show>

            <Show when={view() === 'backtest'}>
                <Table
                    columns={BACKTEST_COLUMNS}
                    rows={backtests()}
                    empty='No fund has both a price history and a payout on file'
                    footer={
                        <Text tone='muted'>
                            {formatCurrency(amount())} at each fund’s first close, {reinvest() ? 'distributions reinvested at the pay-date close' : 'distributions taken as cash'}. Return is cumulative and per year — a simple average, not a compounded rate.
                        </Text>
                    }
                />
            </Show>
        </Panel>
    );
};

export default History;
